import {
  CartesianMarkerProps,
  CssMixBlendMode,
  DatumValue,
  DotsItemSymbolComponent,
  Margin,
  PropertyAccessor,
  SvgFillMatcher,
  ValueFormat,
} from "@nivo/core";
import type { PartialTheme as NivoTheme } from "@nivo/theming";
import {
  ButtonProps,
  CheckboxOptionType,
  FormInstance,
  ImageProps,
  QRCodeProps,
  RateProps,
  SelectProps,
  SliderSingleProps,
  SwitchProps,
  UploadProps,
} from "antd";
import { DefaultOptionType } from "antd/es/select";
import { SliderRangeProps } from "antd/es/slider";
import {
  ChangeEventHandler,
  CSSProperties,
  Dispatch,
  ForwardRefExoticComponent,
  Key,
  MouseEventHandler,
  RefObject,
  ReactElement,
  ReactNode,
  RefAttributes,
  SetStateAction,
  JSX,
} from "react";
import { InheritedColorConfig, OrdinalColorScaleConfig } from "@nivo/colors";
import { GridLabelComponent, PointData, RadarSvgProps } from "@nivo/radar";
import { ComputedDatum, SunburstLayerId, defaultProps as SunburstProps } from "@nivo/sunburst";

import { LegendProps } from "@nivo/legends";
import { ScaleSpec, TicksSpec } from "@nivo/scales";
import { AnimationConfig } from "@react-spring/core";
import { LineSeries, LineSvgLayer, Point, PointTooltipComponent, SliceTooltipComponent, PointOrSliceMouseHandler } from "@nivo/line";
import { AxisProps } from "@nivo/axes";
import { TooltipPlacement, TooltipProps } from "antd/es/tooltip";
import { TextAreaProps, SearchProps } from "antd/es/input";
import { LinkProps } from "antd/es/typography/Link";
import { ImageSettings, QRStatus, StatusRenderInfo } from "antd/es/qr-code/interface";
import { LineDatum, TimeLineSeries } from "../list/defaultType";
import { PHIconProps } from "@/components/common/icon/icon";
type PointMouseHandler<TDatum = any> = (point: TDatum & { serieId: string }, event: React.MouseEvent) => void;
export type DataCols = (string | DataColumn)[] | DataColumnRecord;
export type DataColumnList = DataColumnField[];
export type DataColumn = {
  field: string;
} & DataColumnField &
  DataInputCommonParams;
export type DataColumnRecord = Record<string, DataColumnField>;
export type DataColumnField = {
  className?: string;
  dataLink?: string | any;
  dataLinkField?: boolean;
  label?: DataElement | string;
  labelInput?: DataInputCommonParams;
  labelMyAuto?: boolean;
  labelTransition?: boolean;
  order?: number;
  orderDesc?: boolean;
} & DataInputCommonParams;
export type DataInputParams = {
  value?: InputTypeValue | InputTypeValue[];
  rangeValue?: InputTypeValue | InputTypeValue[];
  values: any;
  subValues?: any;
  codeGroups?: Record<string, any[]> | null;
  dataGuideType?: DataGuideType;
  form?: FormInstance<any>;
  setTimer?: Function;
  setValuesField?: Record<string, Function>;
  setValueList?: Record<string, Function>;
  selectWithFields?: string[] | undefined;
  setSubData?: Function;
  formMemo?: Record<string, any>;
  formFunction?: Record<string, Record<string, Function>>;
  initFunction?: RefObject<Function[] | undefined>;
  resetFunction?: Record<string, Function>;
  inputMode?: string;
  loading?: boolean;
  //ref?: any;
  id?: string;
  name?: string;
  pathname?: string;
  index?: number;
} & DataInputCommonParams;
export type DataInputCommonParams = {
  nextInputHidden?: boolean;
  nextItemHidden?: boolean;
  hiddenCheckValue?: boolean;
  asChild?: boolean;
  rangeField?: string;
  subList?:
    | boolean
    | number
    | string
    | {
        target: string;
        valueConvert?: boolean;
        labelField?: string;
        labelTransition?: boolean;
        valueField?: string | Record<string, string>;
      };
  subListEmptyLabel?: string | DataElement | undefined;
  subListEmptyValue?: ReactNode;
  subListTarget?: string;
  subListName?: string;
  subListOrgName?: string;
  subListData?: any;
  subListProp?:
    | boolean
    | number
    | string
    | { target: string; labelField?: string; labelTransition?: boolean; valueField?: string | Record<string, string> };
  subListKeys?: string | string[];
  indexKey?: string | string[];
  indexLabelKey?: string | string[];
  fieldId?: string | Record<string, DataInputCommonParams>;
  fieldIds?: string[];
  fieldList?: string[][] | Record<string, string>[];
  valueList?: { label?: string; value: InputTypeValue; disabled?: boolean; indeterminate?: boolean }[] | DefaultOptionType[];
  valueType?: InputTypeValueType;
  inputType?: InputTypeData;
  dataInputMode?: string;
  sign?: boolean;
  step?: number | string;
  scale?: number;
  maxValue?: number | string;
  minValue?: number | string;
  nullValue?: number | string;
  minLength?: number;
  maxLength?: number;
  readInput?: boolean;
  readInputDisabled?: boolean;
  valueJoin?: string;
  codeGroup?: string;
  selectFieldFilter?: string | RegExp | Record<string, string | RegExp> | ((value: any, index: number, array: any[]) => unknown);
  selectFieldRequired?: string | string[];
  codeList?: Record<string, InputTypeValue>;
  //dataDBProp?: indexDBProp;
  read?: DataCommonInputParams;
  edit?: DataCommonInputParams;
  create?: DataCommonInputParams;
  update?: DataCommonInputParams;
} & DataCommonInputParams;
export type DataProp = {
  option?: {};
  host?: boolean;
  fixedParam?: Record<string, any>;
  valueParam?: Record<string, string>;
  requiredParam?: string[];
  userAuth?: boolean;
  check?: (e: any) => boolean;
  firstReload?: boolean;
  start?: Function;
  process?: (e: any) => any;
  error?: (e: any) => void;
  load?: (data: any, value_data: any, field?: string, index?: number, change?: boolean) => void;
  store?: boolean | string;
  term?: number;
};
export type subsLabel = { field: string; subs: string };
export type indexedDBProp = {
  name: string;
  version: number;
  store: string;
  index: string | string[];
  indexName: string;
  exIndex?: Record<string, string | string[]>;
  term: number;
};
export type UserAuth = Record<string, { seq: number; usertype: string }>;
export type DisplayCondition =
  | boolean
  | "change"
  | "Null"
  | "NullOr"
  | "notNull"
  | "notNullAnd"
  | "true"
  | "false"
  | Record<
      string,
      boolean | string | number | (string | number | null)[] | { value: string | number | (string | number | null)[]; equals: boolean }
    >
  | (
      | "Null"
      | "NullOr"
      | "notNull"
      | "notNullAnd"
      | "true"
      | "false"
      | Record<
          string,
          boolean | string | number | (string | number | null)[] | { value: string | number | (string | number | null)[]; equals: boolean }
        >
    )[]
  | Function;
export type DataGuideType = "FULL" | "SIMPLE" | "NONE";
export type GuideTypeStr = "FULL" | "SIMPLE" | "ALL";
export type GuideType = GuideTypeStr | true | false;
export type dateFormatterEx =
  | string
  | {
      hour?: string;
      hourZz?: string;
      hourzZ?: string;
      hourZZ?: string;
      minute?: string;
      minuteZ?: string;
      second?: string;
    };
type switchButtonChildrenType = {
  title?: string;
  type?: ButtonProps["type"];
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
  shape?: ButtonProps["shape"];
  iconType?: string;
  iconName?: string;
};
export type DataCommonInputParams = {
  userSeq?: number;
  userType?: string[];
  userAuth?: UserAuth;
  checkUserType?: string[];
  checkUserAuth?: Record<string, string[]>;
  enabledUserType?: string[];
  enabledUserAuth?: Record<string, string[]>;
  codeGroupLabel?: string | "feat" | string[];
  codeGroupJoinPrev?: string;
  codeGroupJoinNext?: string;
  spaceClassName?: string;
  itemClassName?: string;
  iconName?: string;
  label?: DataElement | string;
  labelInput?: DataInputCommonParams;
  labelMyAuto?: boolean;
  labelNamePrev?: string;
  labelPrev?: string | DataElement;
  labelNext?: string | DataElement;
  labelUse?: boolean;
  labelTransition?: boolean;
  labelClassName?: string;
  labelStyle?: CSSProperties;
  valueClassName?: string;
  valueStyle?: CSSProperties;
  defaultValue?: InputTypeValue | InputTypeValue[];
  rangeDefaultValue?: InputTypeValue | InputTypeValue[];
  variant?: InputVariant;
  password?: boolean;
  readOnly?: boolean;
  noItem?: boolean;
  widthFull?: boolean;
  flexClassName?: string;
  inputClassName?: string;
  inputStyle?: CSSProperties;
  inputNumberClassName?: string;
  type?: string;
  locale?: string;
  transition?: boolean;
  transitionGroup?: string;
  unuseCodeList?: string[];
  display?: DisplayCondition;
  guide?: GuideType;
  show?: DisplayCondition;
  displayChange?: boolean;
  displayLabel?: DisplayCondition;
  displayValue?: DisplayCondition;
  displayCheckIds?: string[];
  displayCheckIndexIds?: string[];
  disabled?: boolean;
  prevNewLine?: boolean | string;
  prev?: DataInputElement | string;
  next?: DataInputElement | string;
  unit?: DataInputElement | string;
  unitPrev?: DataInputElement | string;
  emptyUnit?: boolean;
  unitProp?: {
    unitList: string[] | DefaultOptionType[];
    defaultUnit?: string;
    convertUnit?: string;
    placeholder?: string;
    placeholderTransition?: boolean;
    tooltip?: string;
    tooltipTransition?: boolean;
    tooltipField?: boolean;
    tooltipProps?: TooltipProps;
    unitButtonClassName?: string;
    onChange?: (event: any) => void;
  };
  unitChange?: boolean;
  inputPrev?: DataInputElement | string;
  inputNext?: DataInputElement | string;
  minButton?: ButtonProps;
  maxButton?: ButtonProps;
  placeholder?: string;
  placeholderTransition?: boolean;
  placeholderForce?: boolean;
  rangePlaceholder?: string;
  formatter?: (value: any, info: { userTyping: boolean; input: string }) => string;
  parser?: (displayValue: string | undefined) => number;
  onClick?: (event: any) => void;
  onChange?: (event: any) => void;
  onPressEnter?: (event: any) => void;
  onChangeComplete?: (event: any) => void;
  autocomplete?: boolean;
  indeterminate?: boolean;
  dateFormatter?: dateFormatterEx;
  allowHalf?: boolean;
  allowClear?: boolean;
  showCount?: boolean;
  character?: ReactNode | ((props: RateProps) => ReactNode);
  elementProp?: ElementProps;
  fixedValue?: any;
  dateProp?: DateProps;
  timeProp?: TimeProps;
  switchProp?: SwitchProps;
  switchButtonProp?: buttonCommonProps & {
    checkedChildren?: switchButtonChildrenType;
    unCheckedChildren?: switchButtonChildrenType;
  };
  sliderProp?: SliderProps;
  qrProp?: QRProps;
  linkOption?: LinkOption;
  checkboxOption?: (string | number | CheckboxOptionType<any>)[];
  dataUrl?: string;
  dataValueList?: string | string[];
  dataProp?: DataProp;
  dataLabel?: {
    label: string | subsLabel | (string | subsLabel)[];
    value: string;
    labelJoin?: string | string[];
    labelJoinNext?: string | string[];
    labelSort?:
      | -1
      | boolean
      | ((
          a: { value: any; label: string | JSX.Element; label_str: string },
          b: { value: any; label: string | JSX.Element; label_str: string },
        ) => number);
  };
  scrollMargin?: string | number;
  selectFilter?:
    | string
    | string[]
    | RegExp
    | Record<string, string | string[] | RegExp>
    | ((value: any, index: number, array: any[]) => unknown);
  selectChangeSync?: string | string[] | Record<string, string>;
  selectChangeLoad?: string | string[];
  selectChangeDelay?: number;
  selectWithLoad?: string | string[];
  selectProp?: SelectProps;
  selectPropOption?: {
    multiValueNumber?: boolean;
    setEmptyValue?: boolean | number;
    reloadTitle?: string;
    reloadTitleTransition?: boolean;
    reloadButton?: boolean;
    reloadButtonType?: "link" | "text" | "default" | "primary" | "dashed" | undefined;
    reloadButtonName?: string;
    reloadButtonClassName?: string;
    reloadButtonFront?: boolean;
    reloadButtonClick?: (
      setValueList: Dispatch<SetStateAction<DefaultOptionType[] | undefined>>,
      setLoading: (value: SetStateAction<boolean>) => void,
    ) => void;
  };
  linkProp?: LinkProps;
  linkPropOption?: {
    text?: string | JSX.Element;
    title?: string;
    titleTransition?: boolean;
    titleField?: string;
    linkButtonName?: string;
    linkButtonIcon?: boolean;
    linkButtonClassName?: string;
    linkButtonFront?: boolean | "just";
    linkButtonTitle?: string;
    linkButtonTitleTooltip?: boolean;
    linkButtonUse?: boolean;
    linkButtonNewTab?: boolean;
    newTab?: boolean;
  };
  copyProp?: {
    copyTitle?: string;
    copyTitleTransition?: boolean;
    copyButton?: boolean;
    copyButtonIcon?: boolean;
    copyButtonName?: string;
    copyButtonTitleTooltip?: boolean;
    copyButtonClassName?: string;
    copyButtonFront?: boolean;
    copyButtonClick?: Function;
  };
  clearProp?: {
    clearTitle?: string;
    clearTitleTransition?: boolean;
    clearButton?: boolean;
    clearButtonName?: string;
    clearButtonClassName?: string;
    clearButtonFront?: boolean;
    clearButtonClick?: Function;
  };
  inputDirect?:
    | string
    | {
        target: string;
        query?: string;
        autoFocus?: boolean;
        label?: string | React.ReactNode;
        labelStr?: string;
        labelTransition?: boolean;
        value?: string | number;
      };
  calcValueProp?: CalcValueProps;
  radarProp?: RadarProps;
  timelineProp?: TimeLineProps;
  flavorWheelProp?: FlavorWheelProp & {
    target?: string;
    viewMode?: boolean;
    checkReverse?: boolean | string;
    showSimple?: boolean | string;
    wheelRotate?: string | number;
  };
  geoMapProp?: GeoMapProps;
  imageProp?: ImageProps;
  imageDownload?: boolean | string;
  textAreaProp?: TextAreaProps;
  textAreaResize?: boolean;
  textAreaAutoSize?: boolean | object;
  searchProp?: SearchProps;
  uploadProp?: UploadProps;
  tooltip?: boolean | string;
  tooltipPlacement?: TooltipPlacement;
  tooltipTransition?: boolean;
  tooltipField?: boolean;
  tooltipProps?: TooltipProps;
  required?: boolean;
  saveData?: boolean;
  changePass?: boolean;
  writeData?: boolean;
  editData?: boolean;
};
type subInputProps = {
  commonInputProp?: DataInputCommonParams;
  fieldInputProp?: Record<string, DataInputCommonParams>;
  frontInput?: boolean;
};
export type buttonCommonProps = {
  title?: string;
  titleTransition?: boolean;
  titleTransitionGroup?: string;
  buttonName?: string;
  buttonClassName?: string;
  buttonFront?: boolean | null;
  disabled?: boolean;
  buttonClick?: MouseEventHandler<HTMLElement> | undefined;
};
export type nivoCommonProps = {
  theme?: NivoTheme;
  className?: string;
  inputAsChild?: boolean;
  isInteractive?: boolean;
  legends?: LegendProps[];
  motionConfig?: string | Partial<AnimationConfig>; //string; // | Partial<AnimationConfig>;
  isMobile?: boolean;
  isAnimation?: boolean;
} & subInputProps;

export type RadarProps = nivoCommonProps & {
  animate?: boolean;
  keys?: string[];
  indexBy?: PropertyAccessor<Record<string, any>, string>;
  data?: Record<string, unknown>[];
  dataConvert?: boolean;
  valueFormat?: string;
  margin?: Partial<Margin>;
  borderColor?: InheritedColorConfig<{ key: string; color: string }>;
  gridLabelOffset?: number;
  gridLabel?: GridLabelComponent;
  curve?: "basisClosed" | "cardinalClosed" | "catmullRomClosed" | "linearClosed"; //ClosedCurveFactoryId; //    | 'basisClosed'| 'cardinalClosed'| 'catmullRomClosed'| 'linearClosed'
  dotLabelYOffset?: number;
  dotLabelFormat?: ValueFormat<number>;
  dotBorderColor?: InheritedColorConfig<PointData>;
  dotLabel?: PropertyAccessor<PointData, string | number>;
  dotSymbol?: DotsItemSymbolComponent;
  dotSize?: number;
  dotColor?: InheritedColorConfig<PointData>;
  dotBorderWidth?: number;
  enableDotLabel?: boolean;
  colors?: OrdinalColorScaleConfig<{ key: string; index: number }>;
  blendMode?: CssMixBlendMode;
  gridShape?: "linear" | "circular";
  rotation?: number;
  maxValue?: number | "auto";
  fillOpacity?: number;
  //onClick?: (node: any, event: any) => void;
};
export type LineProps = nivoCommonProps & {
  data?: TimeLineSeries[];
  areaOpacity?: boolean;
  areaBaselineValue?: DatumValue;
  axisBottom?: AxisProps<any> | null;
  axisTop?: AxisProps<any> | null;
  axisLeft?: AxisProps<any> | null;
  axisRight?: AxisProps<any> | null;
  areaBlendMode?: string;
  colors?: OrdinalColorScaleConfig;
  curve?: "monotoneX" | "basis" | "cardinal" | "catmullRom" | "linear" | "monotoneY" | "natural" | "step" | "stepAfter" | "stepBefore";
  defs?: { [key: string]: any; id: string }[];
  enableArea?: boolean;
  enableTouchCrosshair?: boolean;
  enableSlices?: false | "x" | "y";
  enablePointLabel?: boolean;
  enableCrosshair?: boolean;
  enableGridX?: boolean;
  enableGridY?: boolean;
  enablePoints?: boolean;
  fill?: { id: string; match: Record<string, unknown> | "*" | SvgFillMatcher<LineDatum> }[];
  gridXValues?: TicksSpec<DatumValue>;
  gridYValues?: TicksSpec<DatumValue>;
  layers?: readonly LineSvgLayer<LineSeries>[];
  lineWidth?: number;
  margin?: Partial<Margin>;
  onClick?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onMouseEnter?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onMouseLeave?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onMouseMove?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onTouchEnd?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onTouchMove?: PointOrSliceMouseHandler<LineSeries> | undefined;
  onTouchStart?: PointOrSliceMouseHandler<LineSeries> | undefined;
  pointLabelYOffset?: number;
  pointBorderColor?: any;
  pointBorderWidth?: number;
  pointSize?: number;
  pointSymbol?: DotsItemSymbolComponent<Point<LineSeries>>; //(props: Readonly<PointSymbolProps>) => ReactNode;
  pointLabel?: PropertyAccessor<Point<LineSeries>, string> | undefined;
  pointColor?: any;
  sliceTooltip?: SliceTooltipComponent<LineSeries>;
  tooltip?: PointTooltipComponent<LineSeries>;
  useMesh?: boolean;
  xFormat?: ValueFormat<DatumValue>;
  xScale?: ScaleSpec | undefined;
  yFormat?: ValueFormat<DatumValue>;
  yScale?: ScaleSpec | undefined;
  markers?: CartesianMarkerProps<DatumValue>[];
};
export type TimeLineXY = { x?: Date; y?: number };
type TimerFunction = { onClick: Function; buttonProp?: ButtonProps; iconProp?: PHIconProps };
type OperationProps = {
  id?: string;
  title?: string;
  operation:
    | ((a: number, b: number | null | undefined) => number | undefined)
    | "ADD"
    | "SUB"
    | "MUL"
    | "DIV"
    | "AVG"
    | "MIN"
    | "MAX"
    | "DIFF_ABS"
    | "DIFF_REL";
  dataA: string;
  dataB?: string;
  compareStr?: string;
  startId?: string;
  endId?: string;
  startTerm?: number;
  endTerm?: number;
  startDate?: Date;
  endDate?: Date;
  unit?: DataInputElement | string;
  scale?: number;
  scaleFixed?: boolean;
  toFixed?: number;
  minValue?: number;
};
type RORProps = {
  rorSeries?: string;
  rorId?: string;
  rorTitle?: string;
  compareStr?: string;
  startId?: string;
  endId?: string;
  startTerm?: number;
  endTerm?: number;
  startDate?: Date;
  endDate?: Date;
  rorTerm?: number;
  rorTermUnit?: number;
  rorScale?: number;
  rorScaleFixed?: boolean;
  toFixed?: number;
  minValue?: number;
};
export type TimeLineProps = LineProps & {
  fixed?: boolean;
  baseTime?: string;
  beforeTime?: string[];
  checkIds?: string | string[];
  closeButton?: boolean;
  calcValues?: string[] | RegExp;
  colorMap?: Record<string, { color: string; width?: number | string; dashArray?: number | string }>;
  checkValues?: (string | number)[];
  checkTimes?: string[];
  showTimes?: Record<string, Record<string, string>>;
  timeLimit?: number;
  timerStart?: boolean | Function;
  timerStop?: Function;
  timerLimit?: boolean | string | string[];
  timerColor?: string;
  timerDelay?: number;
  timerClock?: TimerFunction;
  timerCheck?: TimerFunction;
  timerFind?: TimerFunction;
  // timerButtons?: {};
  transitionId?: boolean;
  hours?: boolean;
  min?: TimeLineXY;
  max?: TimeLineXY;
  hintIds?: string | string[];
  dataOperation?: Record<string, OperationProps>;
  ror?: Record<string, RORProps>;
  checkPoint?: Record<string, Date>;
  checkValuePoint?: number[];
  comparePoint?: Record<string, Record<string, Date>>;
  compareBaseTime?: string;
  compareBaseTimeId?: string;
  compareBaseTimeInput?: string;
  pointLabelLimit?: { min?: number; max?: number };
  pointLabelIdLimit?: Record<string, { min?: number; max?: number }>;
  xMinText?: string;
  xMaxText?: string;
  xFloorScale?: number;
  xFloorConst?: number;
  xTopShowTime?: boolean;
  xBottomTickZeroShow?: boolean;
  xBottomTickValues?: TicksSpec<any> | "checkAll" | undefined;
  yFloorScale?: number;
  yFloorConst?: number;
  yUnit?: string;
  ySeriesUnit?: Record<string, string>;
  ySeriesScale?: string[] | SeriesScaleRecord;
  yViewScale?: number;
  yGridCount?: number;
  beforeId?: string;
  logId?: string;
};
export type SeriesScaleRecord = Record<string, SeriesScaleType>;
export type SeriesScaleType = {
  toFixed?: number;
  subScale?: number;
  // fixScale?: number;
  scale?: number;
  max?: number;
  calcScale?: number;
  yFloorConst?: number;
};

export type FlavorWheelProp = {
  id?: string;
  value?: string;
  cornerRadius?: number;
  layers?: SunburstLayerId[];
  colors?: OrdinalColorScaleConfig<any>;
  colorBy?: "id";
  inheritColorFromParent?: boolean;
  childColor?: {
    from: string;
  };
  borderWidth?: number;
  borderColor?: string;
  enableArcLabels?: boolean;
  arcLabel?: string;
  arcLabelsRadiusOffset?: number;
  arcLabelsSkipAngle?: number;
  arcLabelsTextColor?: {
    theme: string;
  };
  animate?: boolean;
  motionConfig?: string;
  transitionMode?: "startAngle" | "middleAngle" | "endAngle" | "innerRadius" | "centerRadius" | "outerRadius" | "pushIn" | "pushOut";
  isInteractive?: boolean;
  defs?: never[];
  fill?: never[];
  tooltip?: <RawDatum>({ id, formattedValue, color }: ComputedDatum<RawDatum>) => JSX.Element;
  role?: string;
};
export type loadingMode = "init" | "changeGroup" | "reload" | "change" | "changeLoad" | "set" | "changeValues";
export type ElementProps = (DataInputElement & { iconFront?: boolean }) | string;
export type GeoMapProps = nivoCommonProps & {
  country_list?: string | string[];
  country_label?: Record<string, string>;
  select_country?: string | string[];
  emptyElement?: string | DataElement;
  mapDisplay?: boolean;
  mapRescale?: boolean;
  closeButton?: boolean;
  clickMove?: boolean;
};
export type SliderProps = (SliderSingleProps | SliderRangeProps) & {
  autoMark?: boolean;
  valueMark?: boolean | string | DataInputElement;
  valueOverride?: boolean;
  valueStyle?: React.CSSProperties;
};
export type CalcValueMode = "+" | "-" | "*" | "/" | "loss" | "%" | "dtr";
export type CalcValueProps =
  | {
      input1: string | number | CalcValueProps;
      mode: CalcValueMode;
      input2: string | number | CalcValueProps;
      fixed?: number;
      scale?: number;
    }
  | ((values: any) => any);
type DateCommonProps = {
  baseTime?: string;
  nowButton?: boolean;
  nowButtonFront?: boolean;
  nowButtonClick?: Function;
  nowButtonClassName?: string;
  suffixIcon?: ReactNode;
  onOpenChange?: ((open: boolean) => void) | undefined;
} & subInputProps;
export type DateProps = {
  showNow?: boolean;
  showMinute?: boolean;
  showSecond?: boolean;
  showMillisecond?: boolean;
  hourStep?: hourStep;
  minuteStep?: secondStep;
  secondStep?: secondStep;
} & DateCommonProps;
export type TimeProps = {
  showHour?: boolean;
  showNow?: boolean;
  showMinute?: boolean;
  showSecond?: boolean;
  showMillisecond?: boolean;
  hourStep?: hourStep;
  minuteStep?: secondStep;
  secondStep?: secondStep;
} & DateCommonProps;
export type LinkPathName = string | { field: string; path: Record<string, string> } | Function;
export type LinkOption = {
  pathname?: LinkPathName;
  site?: string;
  prevIds?: string | string[];
  useHost?: boolean;
  useEncode?: boolean;
};
export type QRProps = {
  uselink?: boolean;
  linkNewTap?: boolean;
  download?: buttonCommonProps;
  type?: "canvas" | "svg";
  prefixCls?: string;
  icon?: string;
  iconSize?:
    | number
    | {
        width: number;
        height: number;
      };
  bordered?: boolean;
  errorLevel?: "L" | "M" | "Q" | "H";
  status?: QRStatus;
  onRefresh?: () => void;
  statusRender?: (info: StatusRenderInfo) => ReactNode;
  size?: number;
  bgColor?: string;
  fgColor?: string;
  style?: CSSProperties;
  includeMargin?: boolean;
  marginSize?: number;
  imageSettings?: ImageSettings;
  title?: string;
  minVersion?: number;
};
export type step10 = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type step20 = 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19;
export type step30 = 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28 | 29;
export type step40 = 30 | 31 | 32 | 33 | 34 | 35 | 36 | 37 | 38 | 39;
export type step50 = 40 | 41 | 42 | 43 | 44 | 45 | 46 | 47 | 48 | 49;
export type step60 = 50 | 51 | 52 | 53 | 54 | 55 | 56 | 57 | 58 | 59;
export type hourStep = step10 | step20 | 20 | 21 | 22;
export type secondStep = step10 | step20 | step40 | step50 | step50 | 50 | 51 | 52 | 53 | 54 | 55 | 56 | 57 | 58;

export type DataElement = {
  element: string | ReactElement;
  elementType?: "string" | "icon" | "element" | "button" | "switch";
  className?: string;
  display?: boolean | "notNull" | "true" | "false";
  buttonProp?: ButtonProps;
  iconProp?: PHIconProps;
  onClick?: (e: any) => void;
  switchFront?: boolean;
  switchProp?: SwitchProps;
  fixValue?: string | ReactElement;
  transition?: boolean;
  tooltip?: string;
  tooltipTransition?: boolean;
  tooltipProps?: TooltipProps;
  tooltipPlacement?: TooltipPlacement;
};
export type DataInputElement = DataElement & {
  field?: boolean;
  spacing?: string | ReactElement;
};
export type PickerString = "date" | "week" | "month" | "quarter" | "year";
export type PickerType = {
  picker?: PickerString;
  range: boolean;
  showTime: boolean;
  timePicker: boolean;
  multiple: boolean;
  mode?: PickerString | "time" | "decade";
};
export type InputVariant = "outlined" | "borderless" | "filled" | undefined;
export const DatePickerTypeArray = [
  "year",
  "quarter",
  "month",
  "week",
  "date",
  "datetime",
  "time",
  "quarterRange",
  "monthRange",
  "weekRange",
  "dateRange",
  "datetimeRange",
  "yearMulti",
  "quarterMulti",
  "monthMulti",
  "weekMulti",
  "dateMulti",
  "yearRange",
  "timeRange",
];
export const ChartTypeArray = ["radar", "radarInput", "radarSlider", "radarRate", "lineChart", "timelineChart", "flavorWheel", "geoMap"];
export const InputTypeDataArray = [
  "image",
  "element",
  "display",
  "input",
  "link",
  "inputNumber",
  "inputSearch",
  "checkbox",
  "checkboxGroup",
  "checkboxCard",
  "radio",
  "radioButton",
  "radioGroup",
  "radioCard",
  "select",
  "selectSearch",
  "segmentedControl",
  "slider",
  "sliderInput",
  "switch",
  "switchButton",
  "upload",
  "qrcode",
  "rate",
  "rateInput",
  "summary",
  "textArea",
  "textField",
];
export type DatePickerType = (typeof DatePickerTypeArray)[number];
export type ChartType = (typeof ChartTypeArray)[number];
export type InputTypeData = (typeof InputTypeDataArray)[number] | DatePickerType | ChartType;
export type InputTypeValue = number | string | boolean | Record<string, any> | any;
export type InputTypeValueType = "number" | "string" | "stringArray" | "array" | "boolean" | "json";
